import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { AlertTriangleIcon } from '@braskai/icons';
import { Button, IconButton, Modal, ModalActions, ModalContent, ModalWrapper, Typography } from '@braskai/ui-kit';
import { useNavigate } from 'react-router-dom';

import { Close } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';

import { useManageBoostersModalStore } from '@/features/boosters/model/useManageBoostersModalStore';

import { useBalanceStore } from '@/entities/balance';
import { useSettings } from '@/entities/user/currentUser';

import { PATHS } from '@/shared/constants/routes/paths';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { BoosterType } from '@/shared/types/Billing';

import { BOOSTER_TOOLTIP_MAP } from '../../lib/boosterTooltipData';
import { BOOSTERS_HELP_URL } from '../../lib/constants';
import { formatBoosterExpirationDate, formatBoosterPrice } from '../../lib/formatBoosterPrice';
import { ScheduledBoosterChanges } from '../../lib/types';
import { useBoosterConfirmation } from '../../model/useBoosterConfirmation';
import { useBoosters } from '../../model/useBoosters';
import { useBoosterSeatsCounter } from '../../model/useBoosterSeatsCounter';
import { BoosterItem } from '../BoosterItem/BoosterItem';
import { BoosterSeatsCounter } from '../BoosterSeatsCounter/BoosterSeatsCounter';

const buildInitialBoosters = (
  hasPurchasedSRT: boolean,
  hasPurchasedSeats: boolean,
  hasPurchasedPrompting: boolean,
  scheduledBoosterChanges: null | ScheduledBoosterChanges,
): Record<BoosterType, boolean> => ({
  [BoosterType.SRT]: hasPurchasedSRT && !scheduledBoosterChanges?.[BoosterType.SRT],
  [BoosterType.TeamspaceSeat]: hasPurchasedSeats && !scheduledBoosterChanges?.[BoosterType.TeamspaceSeat],
  [BoosterType.TranslationPrompting]: hasPurchasedPrompting && !scheduledBoosterChanges?.[BoosterType.TranslationPrompting],
});

const getBoosterScheduledExpirationDate = (
  type: BoosterType,
  scheduledBoosterChanges: null | ScheduledBoosterChanges,
  defaultSeats: number,
): string | undefined => {
  const scheduledBooster = scheduledBoosterChanges?.[type];
  if (!scheduledBooster) return undefined;

  const formattedDate = formatBoosterExpirationDate(scheduledBooster.update_date);

  if (type === BoosterType.TeamspaceSeat) {
    const totalNewSeats = Math.max(defaultSeats, 1) + (scheduledBooster.new_quantity || 0);
    return `${totalNewSeats} seats active until ${formattedDate} with no additional charges, then ${defaultSeats} seats.`;
  }

  return `Active until ${formattedDate} with no charges`;
};

type ManageBoostersModalComponentProps = {
  onOpenCancelPause: () => void;
};

export const ManageBoostersModalComponent = ({ onOpenCancelPause }: ManageBoostersModalComponentProps) => {
  const navigate = useNavigate();
  const onClose = useManageBoostersModalStore((state) => state.close);
  const openManageBoostersModal = useManageBoostersModalStore((state) => state.open);
  const settings = useSettings();
  const { allBoosters, canPurchaseBoosters, purchasedBoosters, scheduledBoosterChanges } = useBoosters();
  const {
    hasActiveSubscription,
    hasCanceledSubscription,
    hasExpiredSubscription,
    hasPastDueSubscription,
    hasPausedSubscription,
    hasUnpaidSubscription,
  } = useBalanceStore();

  const isSubscriptionInactive =
    hasPausedSubscription || hasCanceledSubscription || hasExpiredSubscription || hasPastDueSubscription || hasUnpaidSubscription;

  const isFailedPayment = hasPastDueSubscription || hasUnpaidSubscription || hasExpiredSubscription

  const hasPurchasedSRT = !!purchasedBoosters?.[BoosterType.SRT];
  const hasPurchasedPrompting = !!purchasedBoosters?.[BoosterType.TranslationPrompting];
  const purchasedSeats = purchasedBoosters?.[BoosterType.TeamspaceSeat]?.quantity || 0;
  const hasPurchasedSeats = purchasedSeats > 0;
  const defaultSeats = settings.scope_settings.available_features.spaces.metadata?.user_limit ?? 0;

  const { decrement, increment, seatsCount, setSeatsCount } = useBoosterSeatsCounter();
  const { openBoosterConfirmation } = useBoosterConfirmation();

  const [selectedBoosters, setSelectedBoosters] = useState<Record<BoosterType, boolean>>(
    () => buildInitialBoosters(hasPurchasedSRT, hasPurchasedSeats, hasPurchasedPrompting, scheduledBoosterChanges),
  );

  useEffect(() => {
    setSelectedBoosters(buildInitialBoosters(hasPurchasedSRT, hasPurchasedSeats, hasPurchasedPrompting, scheduledBoosterChanges));
    setSeatsCount(purchasedSeats || 1);
  }, [hasPurchasedSRT, hasPurchasedSeats, hasPurchasedPrompting, purchasedSeats, setSeatsCount, scheduledBoosterChanges]);

  const isContinueDisabled = useMemo(() => {
     if (!canPurchaseBoosters) return true;
    const initialSRT = hasPurchasedSRT && !scheduledBoosterChanges?.[BoosterType.SRT];
    const initialPrompting = hasPurchasedPrompting && !scheduledBoosterChanges?.[BoosterType.TranslationPrompting];
    const initialSeatsActive = hasPurchasedSeats && !scheduledBoosterChanges?.[BoosterType.TeamspaceSeat];

    const isSRTChanged = selectedBoosters[BoosterType.SRT] !== initialSRT;
    const isPromptingChanged = selectedBoosters[BoosterType.TranslationPrompting] !== initialPrompting;

    const currentSeats = selectedBoosters[BoosterType.TeamspaceSeat] ? seatsCount : 0;
    const initialSeats = initialSeatsActive ? purchasedSeats : 0;
    const isSeatsChanged = currentSeats !== initialSeats;

    return !(isSRTChanged || isPromptingChanged || isSeatsChanged);
  }, [selectedBoosters, seatsCount, hasPurchasedSRT, hasPurchasedPrompting, hasPurchasedSeats, purchasedSeats, scheduledBoosterChanges, canPurchaseBoosters]);


  const toggleBooster = (id: BoosterType) => {
    setSelectedBoosters((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const incrementSeats = (e: MouseEvent) => {
    e.stopPropagation();
    increment();
  };

  const decrementSeats = (e: MouseEvent) => {
    e.stopPropagation();
    decrement();
  };

  const handleAddAndContinue = () => {
    MixpanelEvents.boostersContinueClick({
      boosters_prompting: selectedBoosters[BoosterType.TranslationPrompting],
      boosters_seats: selectedBoosters[BoosterType.TeamspaceSeat] ? seatsCount : 0,
      boosters_srt: selectedBoosters[BoosterType.SRT],
      source: 'manage_boosters_modal',
    });
    onClose();

    openBoosterConfirmation({
      seatsCount,
      selectedBoosters,
      onCancel: openManageBoostersModal,
    });
  };

  const warningMessage = (() => {
    if (hasPausedSubscription) return 'Boosters are paused due to paused subscription';
    if (hasCanceledSubscription) return 'Boosters are unavailable due to canceled subscription';
    if (hasExpiredSubscription) return 'Boosters are unavailable due to expired subscription';
    if (hasPastDueSubscription || hasUnpaidSubscription) return 'Paused due to failed payment';
    return '';
  })();

  const buttonText = (() => {
    if (hasPausedSubscription) return 'Resume subscription';
    if (hasCanceledSubscription || hasExpiredSubscription) return 'Renew subscription';
    if (hasUnpaidSubscription || hasPastDueSubscription) return 'Manage billing';
    return '';
  })();

  const handleManageBilling = useCallback(() => {
    onClose();
    if (hasPausedSubscription) {
      onOpenCancelPause();
    } else if (!hasActiveSubscription && hasCanceledSubscription) {
      navigate(`/${PATHS.pricing}`);
    } else {
      navigate(`/${PATHS.account}`, { replace: true });
    }
  }, [navigate, onClose, hasPausedSubscription, hasActiveSubscription, onOpenCancelPause, hasCanceledSubscription]);

  return (
    <Modal size="xs" open onClose={onClose}>
      <ModalContent>
        <Stack alignItems="center" flexDirection="row" justifyContent="space-between">
          <Typography color="grey.900" variant="title-4-md">
            Manage boosters
          </Typography>
          <IconButton onClick={onClose}>
            <Close height={14} width={14} />
          </IconButton>
        </Stack>
        <Stack>
          {/* Description */}
          <Stack gap={8} mb={8}>
            <Typography color="grey.500" variant="body-2">
              Boost your work without changing your plan — choose the tools you want to add to your workflow.
              <Box
                component="a"
                href={BOOSTERS_HELP_URL}
                ml={5}
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
                target="_blank"
              >
                <Typography color="primary.700" component="span" variant="body-2-md">
                  Learn more
                </Typography>
              </Box>
            </Typography>
          </Stack>

          {/* Boosters List */}
          <Stack gap={8}>
            {allBoosters && (
              <>
                {allBoosters[BoosterType.SRT] && (
                  <BoosterItem
                    title="SRT Upload & Download"
                    description="Upload and download corrected transcript"
                    disabled={isSubscriptionInactive || !canPurchaseBoosters}
                    isChecked={selectedBoosters[BoosterType.SRT]}
                    price={`${formatBoosterPrice(allBoosters, BoosterType.SRT)} /month`}
                    scheduledChangeText={getBoosterScheduledExpirationDate(BoosterType.SRT, scheduledBoosterChanges, defaultSeats)}
                    tooltipProps={BOOSTER_TOOLTIP_MAP[BoosterType.SRT]}
                    onChange={() => toggleBooster(BoosterType.SRT)}
                  />
                )}

                {allBoosters[BoosterType.TranslationPrompting] && (
                  <BoosterItem
                    title="Translation prompting"
                    description="Match your dubbed videos your brand voice"
                    disabled={isSubscriptionInactive || !canPurchaseBoosters}
                    isChecked={selectedBoosters[BoosterType.TranslationPrompting]}
                    price={`${formatBoosterPrice(allBoosters, BoosterType.TranslationPrompting)} /month`}
                    scheduledChangeText={getBoosterScheduledExpirationDate(BoosterType.TranslationPrompting, scheduledBoosterChanges, defaultSeats)}
                    tooltipProps={BOOSTER_TOOLTIP_MAP[BoosterType.TranslationPrompting]}
                    onChange={() => toggleBooster(BoosterType.TranslationPrompting)}
                  />
                )}

                {allBoosters[BoosterType.TeamspaceSeat] && (
                  <BoosterItem
                    title="Extra seat for Teamspaces"
                    rightContent={
                      selectedBoosters[BoosterType.TeamspaceSeat] ? (
                        <BoosterSeatsCounter seatsCount={seatsCount} onDecrement={decrementSeats} onIncrement={incrementSeats} />
                      ) : null
                    }
                    description="Collaborate with your team on shared projects seamlessly"
                    disabled={isSubscriptionInactive || !canPurchaseBoosters}
                    isChecked={selectedBoosters[BoosterType.TeamspaceSeat]}
                    price={`${formatBoosterPrice(allBoosters, BoosterType.TeamspaceSeat, seatsCount)} /month`}
                    scheduledChangeText={getBoosterScheduledExpirationDate(BoosterType.TeamspaceSeat, scheduledBoosterChanges, defaultSeats)}
                    tooltipProps={BOOSTER_TOOLTIP_MAP[BoosterType.TeamspaceSeat]}
                    onChange={() => toggleBooster(BoosterType.TeamspaceSeat)}
                  />
                )}
              </>
            )}
          </Stack>
        </Stack>
      </ModalContent>
      {isSubscriptionInactive ? (
        <Stack
          sx={{
            backgroundColor: isFailedPayment ? '' : 'primary.50',
            borderRadius: '12px',
            mx: 24,
            my: 20,
            px: 16,
            py: 12,
          }}
          alignItems="center"
          border="1px solid"
          borderColor={isFailedPayment ? 'error.200' : 'primary.200'}
          borderRadius={12}
          direction="row"
          justifyContent="space-between"
        >
          <Stack alignItems="center" direction="row" gap={10}>
            <Stack color={isFailedPayment ? 'error.700' : 'primary.700'} flexShrink={0}>
              <AlertTriangleIcon height={20} width={20} />
            </Stack>
            <Typography color={isFailedPayment ? 'error.700' : 'primary.700'} maxWidth={210} variant="body-2-md">
              {warningMessage}
            </Typography>
          </Stack>
          <Button
            size="sm"
            sx={{ borderColor: isFailedPayment ? 'error.200' : 'primary.200', color: isFailedPayment ? 'error.700' : 'common.white', flexShrink: 0 }}
            variant={isFailedPayment ? 'outlined' : 'primary'}
            onClick={handleManageBilling}
          >
            {buttonText}
          </Button>
        </Stack>
      ) : (
        <ModalActions>
          <Button disabled={isContinueDisabled} size="lg" fullWidth onClick={handleAddAndContinue}>
            Save and continue
          </Button>
        </ModalActions>
      )}
    </Modal>
  );
};

type ManageBoostersModalProps = {
  onOpenCancelPause: () => void;
};

export const ManageBoostersModal = ({ onOpenCancelPause }: ManageBoostersModalProps) => (
  <ModalWrapper hook={useManageBoostersModalStore}>
    <ManageBoostersModalComponent onOpenCancelPause={onOpenCancelPause} />
  </ModalWrapper>
);
